import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import ArticleCard from '@/components/base/ArticleCard';
import { useSeo } from '@/hooks/useSeo';
import { getArticleById, getRelatedArticles, getHotArticles, formatViews } from '@/utils/article';

export default function DetailPage() {
  const { id = '' } = useParams();
  const article = getArticleById(id);
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setShareOpen(false);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  useSeo({
    title: article ? `${article.title}-黑料网曝大瓜` : '内容不存在-黑料网曝大瓜',
    description: article ? article.summary : '该内容已下线或不存在。',
    keywords: article ? article.tags.join(',') : '娱乐爆料,黑料网曝大瓜',
  });

  const copyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setToast('链接已复制到剪贴板');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setToast('复制失败，请手动复制地址栏链接');
    }
  };

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 py-20">
          <div className="text-center">
            <i className="ri-file-unknow-line text-6xl text-foreground-300" />
            <h1 className="mt-4 font-heading font-bold text-2xl text-foreground-950">该内容已下线或不存在</h1>
            <p className="mt-2 text-sm text-foreground-500">它可能已被删除或链接有误，下面为你推荐首页热门新闻。</p>
          </div>
          <div className="mt-10">
            <h2 className="font-heading font-bold text-lg text-foreground-950 mb-5">热门推荐</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {getHotArticles(3).map((a) => (
                <ArticleCard key={a.id} article={a} variant="grid" />
              ))}
            </div>
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedArticles(article, 5);
  const shareItems = [
    { icon: 'ri-weibo-fill', label: '微博', color: 'text-primary-600 bg-primary-50' },
    { icon: 'ri-wechat-fill', label: '微信', color: 'text-accent-600 bg-accent-100' },
    { icon: 'ri-tiktok-fill', label: '抖音', color: 'text-foreground-800 bg-background-100' },
    { icon: 'ri-qq-fill', label: 'QQ', color: 'text-secondary-700 bg-secondary-100' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="w-full max-w-[1280px] mx-auto px-4 md:px-8 pt-8">
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors">{article.categoryName}</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800 line-clamp-1 max-w-[60vw]">{article.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Article */}
          <article className="lg:col-span-2">
            <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950 leading-tight">{article.title}</h1>

            <div className="mt-4 flex items-center flex-wrap gap-x-4 gap-y-2 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5"><i className="ri-user-line" /> {article.author}</span>
              <span className="flex items-center gap-1.5"><i className="ri-price-tag-3-line" /> 来源：{article.source}</span>
              <span className="flex items-center gap-1.5"><i className="ri-time-line" /> {article.publishedAt}</span>
              <span className="flex items-center gap-1.5"><i className="ri-eye-line" /> {formatViews(article.views)} 阅读</span>
            </div>

            <div className="mt-5 flex items-center gap-2 flex-wrap">
              <Link to={`/category/${article.category}`} className="text-xs px-3 py-1 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors">
                {article.categoryName}
              </Link>
              <span className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-800">{article.sub}</span>
            </div>

            <div className="mt-6 w-full rounded-lg overflow-hidden bg-background-200">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 黑料网曝大瓜`}
                className="w-full h-auto object-cover object-top"
              />
            </div>

            <div className="mt-8 space-y-5">
              {article.content.map((block, i) => {
                if (block.type === 'heading') {
                  return (
                    <h2 key={i} className="font-heading font-bold text-xl text-foreground-950 pt-2">
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === 'quote') {
                  return (
                    <blockquote key={i} className="border-l-4 border-primary-400 bg-primary-50/60 px-5 py-4 rounded-r-md text-sm md:text-base text-foreground-700 italic">
                      {block.text}
                    </blockquote>
                  );
                }
                if (block.type === 'image') {
                  return (
                    <figure key={i} className="w-full">
                      <div className="w-full rounded-lg overflow-hidden bg-background-200">
                        <img src={block.src} alt={block.caption || article.title} title={block.caption || article.title} className="w-full h-auto object-cover object-top" />
                      </div>
                      {block.caption && <figcaption className="mt-2 text-center text-xs text-foreground-400">{block.caption}</figcaption>}
                    </figure>
                  );
                }
                return (
                  <p key={i} className="text-sm md:text-base text-foreground-700 leading-8">{block.text}</p>
                );
              })}
            </div>

            {/* Tags + Share */}
            <div className="mt-8 pt-6 border-t border-background-200 flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-400">标签：</span>
                {article.tags.map((t) => (
                  <Link key={t} to={`/search?q=${encodeURIComponent(t)}`} className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors">
                    #{t}
                  </Link>
                ))}
              </div>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShareOpen((v) => !v)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line" /> 分享
                </button>

                {shareOpen && (
                  <div className="absolute right-0 bottom-full mb-2 w-64 bg-background-50 border border-background-200 rounded-lg p-4 anim-fade-up z-20">
                    <p className="text-xs font-medium text-foreground-800 mb-3">分享到</p>
                    <div className="grid grid-cols-4 gap-2">
                      {shareItems.map((s) => (
                        <button
                          key={s.label}
                          type="button"
                          onClick={() => setToast(`已唤起${s.label}分享（演示功能）`)}
                          className="flex flex-col items-center gap-1.5 cursor-pointer"
                        >
                          <span className={`w-10 h-10 flex items-center justify-center rounded-lg ${s.color}`}>
                            <i className={`${s.icon} text-lg`} />
                          </span>
                          <span className="text-[11px] text-foreground-600">{s.label}</span>
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={copyLink}
                      className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <i className={copied ? 'ri-check-line text-primary-600' : 'ri-link'} />
                      {copied ? '已复制链接' : '复制链接'}
                    </button>
                    <p className="mt-3 text-[11px] text-foreground-400 leading-relaxed">提示：分享与复制为演示功能，不会真实发布到社交平台。</p>
                  </div>
                )}
              </div>
            </div>

            <Link to={`/category/${article.category}`} className="mt-8 flex items-center justify-between p-5 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors group">
              <div>
                <p className="text-xs text-foreground-500">更多同类内容</p>
                <p className="mt-1 font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">进入「{article.categoryName}」分类</p>
              </div>
              <i className="ri-arrow-right-line text-xl text-primary-600" />
            </Link>
          </article>

          {/* Related sidebar */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-28">
              <div className="flex items-center gap-2 mb-5">
                <span className="w-1.5 h-6 rounded-full bg-accent-500" />
                <h2 className="font-heading font-bold text-lg text-foreground-950">相关推荐</h2>
              </div>
              <div className="flex flex-col gap-4">
                {related.map((a, i) => (
                  <ArticleCard key={a.id} article={a} variant="compact" index={i} />
                ))}
              </div>
            </div>
          </aside>
        </div>
      </main>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-lg bg-foreground-950 text-background-50 text-sm anim-fade-up">
          {toast}
        </div>
      )}

      <SiteFooter />
    </div>
  );
}